import React, { useState, useEffect } from 'react';
import { Card, Progress, Button, Typography, Space, Tag, Spin } from 'antd';
import { DownloadOutlined, LoadingOutlined, CheckCircleOutlined, ExclamationCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { videoApi } from '../services/api';

const { Text } = Typography;

/**
 * Export Progress Card Component
 * Displays real-time progress of async export job
 * Polls status every 3 seconds until complete
 */
const ExportProgressCard = ({ exportJobId, onComplete, onError }) => {
  const [exportJob, setExportJob] = useState(null);
  const [polling, setPolling] = useState(true);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    if (!polling || !exportJobId) return;
    
    let pollInterval;
    
    const fetchStatus = async () => {
      try {
        const status = await videoApi.getExportJobStatus(exportJobId);
        setExportJob(status);
        setLoading(false);
        
        // Stop polling when terminal state reached
        if (status.status === 'ready') {
          setPolling(false);
          onComplete?.(status);
        } else if (status.status === 'failed' || status.status === 'expired') {
          setPolling(false);
          onError?.(status.error_message || `Export ${status.status}`);
        }
      } catch (error) {
        console.error('Export status polling error:', error);
        setLoading(false);
        setPolling(false);
        onError?.(error.message);
      }
    };
    
    // Initial fetch
    fetchStatus();
    
    // Poll every 3 seconds
    pollInterval = setInterval(fetchStatus, 3000);
    
    return () => {
      if (pollInterval) clearInterval(pollInterval);
    };
  }, [exportJobId, polling, onComplete, onError]);
  
  if (loading) {
    return (
      <Card style={{ marginBottom: '1rem' }}>
        <Space>
          <Spin />
          <Text>Loading export status...</Text>
        </Space>
      </Card>
    );
  }
  
  if (!exportJob) return null;
  
  const statusConfig = {
    pending: { color: 'blue', icon: <ClockCircleOutlined />, text: 'Pending' },
    processing: { color: 'orange', icon: <LoadingOutlined spin />, text: 'Processing' },
    ready: { color: 'green', icon: <CheckCircleOutlined />, text: 'Ready' },
    failed: { color: 'red', icon: <ExclamationCircleOutlined />, text: 'Failed' },
    expired: { color: 'default', icon: <ExclamationCircleOutlined />, text: 'Expired' }
  };
  
  const status = statusConfig[exportJob.status] || statusConfig.pending;
  
  // Format file size
  const fileSizeMB = exportJob.output_file_size 
    ? (exportJob.output_file_size / 1024 / 1024).toFixed(2) 
    : null;
  
  // Format expiry time
  const expiresAt = exportJob.download_expires_at 
    ? new Date(exportJob.download_expires_at).toLocaleString()
    : null;
  
  return (
    <Card 
      title={
        <Space>
          <span>Export Progress</span>
          <Tag icon={status.icon} color={status.color}>{status.text}</Tag>
        </Space>
      }
      extra={
        exportJob.status === 'ready' && exportJob.download_url && (
          <Button 
            type="primary" 
            icon={<DownloadOutlined />}
            href={exportJob.download_url}
            target="_blank"
            rel="noopener noreferrer"
          >
            Download ZIP
          </Button>
        )
      }
      style={{ marginBottom: '1rem' }}
    >
      <Space direction="vertical" style={{ width: '100%' }}>
        <Progress 
          percent={exportJob.progress.percentage} 
          status={exportJob.status === 'failed' ? 'exception' : (exportJob.status === 'ready' ? 'success' : 'active')}
          strokeColor={exportJob.status === 'failed' ? '#ff4d4f' : (exportJob.status === 'ready' ? '#52c41a' : '#1890ff')}
        />
        
        <Space split="|">
          <Text>
            <strong>{exportJob.progress.completed}/{exportJob.progress.total}</strong> videos processed
          </Text>
          {exportJob.progress.failed > 0 && (
            <Text type="warning">
              <strong>{exportJob.progress.failed}</strong> failed
            </Text>
          )}
          {fileSizeMB && (
            <Text type="secondary">
              {fileSizeMB} MB
            </Text>
          )}
        </Space>
        
        {exportJob.status === 'failed' && exportJob.error_message && (
          <Text type="danger">{exportJob.error_message}</Text>
        )}
        
        {exportJob.status === 'ready' && expiresAt && (
          <Text type="secondary" style={{ fontSize: '12px' }}>
            Download expires: {expiresAt}
          </Text>
        )}
        
        {exportJob.status === 'expired' && (
          <Text type="warning">
            This export has expired and is no longer available for download.
          </Text>
        )}
      </Space>
    </Card>
  );
};

export default ExportProgressCard;



